/**
 * Dinero en el frontend.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LA REGLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El backend envía el dinero como TEXTO ("12.5000"). Aquí se convierte a número
 * SOLO para mostrarlo en pantalla. Nunca se hacen cuentas de dinero con `number`
 * y se envían de vuelta: los cálculos con dinero los hace el backend con
 * `Decimal`, que es exacto.
 *
 *   >>> 0.1 + 0.2
 *   0.30000000000000004     <- por esto no se calcula dinero en JavaScript
 *
 * Si en el futuro el frontend necesita calcular (por ejemplo, el total del
 * carrito sin conexión), se usará una librería de decimales, no `number`.
 */

import { leerTasa, leerTasaContra } from "./direccionDeTasa";
import { normalizarDecimal } from "./decimales";
import type { Currency, TenantCurrency } from "./types";

/** Forma mínima que necesita `formatMoney` para dar formato. */
export type FormatoMoneda = Pick<Currency, "code" | "symbol" | "decimals" | "symbol_before">;

/**
 * Da formato a un monto para mostrarlo al usuario.
 *
 *   formatMoney("12.5000", { code:"USD", symbol:"$", decimals:2, symbol_before:true })
 *     -> "$12,50"
 *   formatMoney("480.0000", { code:"VES", symbol:"Bs", decimals:2, symbol_before:false })
 *     -> "480,00 Bs"
 */
/**
 * El código de una moneda tal como se escribe en un PAPEL: recibos, facturas,
 * cotizaciones, notas y lo que se envía por WhatsApp (lo pidió el dueño,
 * 2026-10-01: «al imprimir factura en Bs que no diga VES, diga BS, en todas
 * las facturas»).
 *
 * «VES» es el código del sistema y se queda en las pantallas de ajustes, donde
 * se elige la moneda; en un papel se escribe como se dice en Venezuela. En la
 * hoja carta los rótulos van en mayúsculas, así que ahí sale «BS».
 */
export function monedaImpresa(codigo: string | null | undefined): string {
  if (!codigo) return "";
  return codigo.toUpperCase() === "VES" ? "Bs" : codigo;
}

/** Lo mismo dentro de un texto ya armado: «1 USD = 56,80 VES» → «… Bs». */
export function conMonedaImpresa(texto: string): string {
  return texto.replace(/\bVES\b/g, "Bs");
}

export function formatMoney(
  monto: string | number | null | undefined,
  moneda: FormatoMoneda,
  opciones: { conCodigo?: boolean; sinSimbolo?: boolean } = {},
): string {
  const valor = toNumber(monto);

  const numero = new Intl.NumberFormat("es-VE", {
    minimumFractionDigits: moneda.decimals,
    maximumFractionDigits: moneda.decimals,
  }).format(valor);

  if (opciones.sinSimbolo) return numero;

  const conSimbolo = moneda.symbol_before
    ? `${moneda.symbol}${numero}`
    : `${numero} ${moneda.symbol}`;

  return opciones.conCodigo ? `${conSimbolo} ${moneda.code}` : conSimbolo;
}

/**
 * El valor que se pone DENTRO de un campo para escribir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ ESTO NO ES `formatMoney`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `formatMoney` es para LEER: pone el símbolo y la coma decimal de aquí
 * («$2,50»). Un `<input>` con eso dentro no se puede escribir ni enviar.
 *
 * Y meter el texto crudo del servidor tampoco: el backend guarda con cuatro
 * decimales —los necesita para las tasas— y el dueño abría su producto y veía
 * **«2.5000»** en la casilla del precio. No está mal, pero parece que está
 * mal, y lo primero que hace cualquiera es borrarlo y escribirlo otra vez.
 *
 * Aquí se recorta a los decimales de la moneda, con punto, que es lo que un
 * campo numérico entiende.
 */
export function montoParaCampo(
  valor: string | number | null | undefined,
  decimales = 2,
): string {
  if (valor === null || valor === undefined || valor === "") return "";
  const n = toNumber(valor);
  return n.toFixed(decimales);
}

/**
 * Lo mismo para una tasa, que necesita más precisión que el dinero.
 *
 * ⚠️ Se quitan los ceros de relleno en vez de recortar a dos: una tasa de
 * 36,5 tiene que poder escribirse 36,5 y una de 0,0025 no se puede redondear a
 * 0,00 sin destruirla. Lo que se quita es «840,0000000000» → «840», que era lo
 * que llenaba la pantalla de Monedas.
 */
export function tasaParaCampo(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return "";
  const n = toNumber(valor);
  if (!n) return "";
  // `parseFloat` sobre el texto recortado quita los ceros sobrantes sin tocar
  // los decimales que sí significan algo.
  return String(Number.parseFloat(n.toFixed(6)));
}

/**
 * Convierte el texto del backend a número, para mostrar o comparar.
 * Nunca uses el resultado para calcular importes que se vayan a cobrar.
 */
export function toNumber(valor: string | number | null | undefined): number {
  if (valor === null || valor === undefined || valor === "") return 0;
  const n = typeof valor === "number" ? valor : Number.parseFloat(valor);
  return Number.isFinite(n) ? n : 0;
}

/**
 * El `step` de un campo de dinero en esa moneda.
 *
 * ⚠️ EN UNA MONEDA SIN DECIMALES, EL PUNTO ES DE MILES (2026-10-30). El peso
 * colombiano se enseña «$244.875» y el cajero lo escribe igual al cerrar la
 * caja: el campo lo leía como 244 pesos con 875 milésimas y el cierre decía
 * «Faltan $244.630». Con `step="1"`, `Campo` deja solo enteros y quita el
 * punto y la coma (ver `normalizarDecimal`). Todo campo de dinero que sepa su
 * moneda lo lleva.
 */
export function pasoDe(decimales: number | null | undefined): "1" | "any" {
  return decimales === 0 ? "1" : "any";
}

/**
 * Cómo entendió Softfi lo que alguien escribió en una casilla de dinero, para
 * enseñarlo al lado: «= 785,15 Bs» (2026-10-30). `null` si no hace falta.
 *
 * ⚠️ POR QUÉ. «785.150» en bolívares puede ser 785 mil o 785 con quince, y
 * Softfi lo lee como decimal. No hay regla automática segura —en dólares
 * «1.500» casi siempre es uno y medio—, así que se le enseña al cajero cómo
 * quedó, y si no es lo que quería lo ve antes de guardar.
 *
 * Solo cuando aporta: si lo escrito lleva punto o coma y no se lee igual que
 * como Softfi lo enseña, o si la cifra tiene cuatro dígitos o más (244875 se
 * lee mejor como «$244.875»). «12,50» o «5» no llevan nada: ya se leen solos.
 *
 * No redondea a los decimales de la moneda: si escribió 12,345 se enseña
 * 12,345, que es lo que se va a guardar.
 */
export function lecturaDeMonto(
  texto: string | number | null | undefined,
  moneda: FormatoMoneda,
): string | null {
  if (texto === null || texto === undefined) return null;
  const escrito = String(texto).trim();
  if (!escrito) return null;
  const limpio = normalizarDecimal(escrito, { decimales: moneda.decimals !== 0 });
  const valor = Number(limpio);
  if (!limpio || !Number.isFinite(valor)) return null;

  const [entero = "", fraccion = ""] = limpio.replace("-", "").split(".");
  const numero = new Intl.NumberFormat("es-VE", {
    minimumFractionDigits: moneda.decimals,
    maximumFractionDigits: Math.min(Math.max(moneda.decimals, fraccion.length), 6),
  }).format(valor);

  const conSeparador = /[.,]/.test(escrito);
  if (!(conSeparador && numero !== escrito) && entero.length < 4) return null;

  return moneda.symbol_before ? `${moneda.symbol}${numero}` : `${numero} ${moneda.symbol}`;
}

/**
 * Da formato a una tasa de cambio.
 *
 * Las tasas se muestran con los decimales que realmente tengan, hasta 4: una
 * tasa de 36,5 debe verse "36,50" y no "36,5000000000", que satura la pantalla.
 */
export function formatRate(tasa: string | null | undefined): string {
  if (!tasa) return "—";
  const valor = toNumber(tasa);
  /*
    ⚠️ HASTA CUATRO DECIMALES, NO DOS (2026-09-25).

    La tasa del BCV llega con cuatro —855,6625— y se enseñaba como «855,66».
    Softfi cobraba con la completa y la pantalla decía la recortada: en
    Moondo, una clienta calculó 108,80 × 855,66 = 93.095,81 Bs, la caja pedía
    93.096,08 (108,80 × 855,6625) y la cajera tuvo que cambiar lo que decía
    la captura. Quien calcula a mano tiene que ver la tasa con la que se
    cobra. Los ceros de más no se enseñan: 36,50 sigue siendo «36,50».
  */
  const decimales = valor >= 1 ? 4 : 6;
  return new Intl.NumberFormat("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimales,
  }).format(valor);
}

/**
 * Frase que explica una tasa en palabras, **en la dirección en que se habla**.
 *
 *   explicarTasa("36.50", "USD", "VES")     ->  "1 USD = 36,50 VES"
 *   explicarTasa("0.000244", "COP", "USD")  ->  "1 USD = 4.098,36 COP"
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LA SEGUNDA LÍNEA ES EL ARREGLO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Antes salía «1 COP = 0,000244 USD», que es la misma cuenta y no dice nada. El
 * dueño lo resumió así: «todo el sistema de tasas se daña, solo funciona con
 * monedas de alto valor».
 *
 * Delante va siempre la moneda que vale más, que es como se dice en la calle:
 * «el dólar está a 4.100». Ver `lib/direccionDeTasa.ts`.
 */
/**
 * Como `explicarTasa`, pero contra la moneda con la que se escribió la tasa.
 * Sin referencia, o si la referencia es la principal, es la de siempre.
 */
export function explicarTasaContra(
  tasa: string | null,
  principal: string,
  moneda: string,
  referencia: { codigo: string; tasa: string } | null,
): string {
  if (!referencia || referencia.codigo === principal) return explicarTasa(tasa, principal, moneda);
  const lectura = leerTasaContra(tasa, moneda, referencia);
  if (!lectura) return explicarTasa(tasa, principal, moneda);
  return `1 ${lectura.izquierda} = ${formatRate(lectura.cuanto)} ${lectura.derecha}`;
}

export function explicarTasa(tasa: string | null, principal: string, moneda: string): string {
  const lectura = leerTasa(tasa, principal, moneda);
  if (!lectura) return "Sin tasa configurada";
  return `1 ${lectura.izquierda} = ${formatRate(lectura.cuanto)} ${lectura.derecha}`;
}

/**
 * Convierte un monto de la moneda de referencia a otra, solo para MOSTRARLO
 * como referencia visual ("≈ 480,00 Bs" debajo del precio en dólares).
 *
 * Es una aproximación para la vista. El monto que se cobra de verdad lo calcula
 * siempre el backend.
 */
export function convertirParaMostrar(
  montoEnPrincipal: string | number,
  tasa: string | null,
): number {
  if (!tasa) return 0;
  return toNumber(montoEnPrincipal) * toNumber(tasa);
}

/**
 * El «≈ Bs» de debajo de un precio o de un total, redondeado COMO SE COBRA.
 *
 * ⚠️ La caja decía «≈ 480,20 Bs» y al cobrar se pedían 481, porque el cobro
 * aplica el redondeo que el dueño eligió en Monedas y la caja no. Lo encontró
 * la auditoría. Dos números distintos para lo mismo, con el cliente delante.
 */
export function equivalenteQueSeCobra(
  montoEnPrincipal: string | number,
  moneda: TenantCurrency,
): number {
  return redondearMoneda(convertirParaMostrar(montoEnPrincipal, moneda.current_rate), moneda);
}

/** Encuentra la moneda de referencia dentro de la lista de monedas del negocio. */
export function monedaPrincipal(monedas: TenantCurrency[]): TenantCurrency | undefined {
  return monedas.find((m) => m.is_primary);
}

/** Convierte una moneda del negocio al formato que necesita `formatMoney`. */
export function comoFormato(moneda: TenantCurrency): FormatoMoneda {
  return {
    code: moneda.currency_code,
    symbol: moneda.symbol,
    decimals: moneda.decimals,
    symbol_before: moneda.symbol_before,
  };
}

/**
 * Como `comoFormato`, pero para escribir una moneda AL LADO de la principal.
 *
 * ⚠️ Si las dos tienen el mismo símbolo —el peso y el dólar son los dos «$»—,
 * esta se escribe con su código: «71.400 COP». Con el símbolo solo, «$71.400»
 * junto a «$21,00» parece otro precio en dólares (lo vio el dueño en la caja,
 * 2026-10-12).
 */
export function comoFormatoJuntoA(
  moneda: TenantCurrency,
  principal: Pick<FormatoMoneda, "symbol">,
): FormatoMoneda {
  const mismoSimbolo = moneda.symbol === principal.symbol;
  return {
    ...comoFormato(moneda),
    symbol: mismoSimbolo ? moneda.currency_code : moneda.symbol,
    symbol_before: mismoSimbolo ? false : moneda.symbol_before,
  };
}

/** Moneda de reserva mientras cargan los datos, para no mostrar la pantalla vacía. */
export const MONEDA_POR_DEFECTO: FormatoMoneda = {
  code: "USD",
  symbol: "$",
  decimals: 2,
  symbol_before: true,
};

/**
 * Cuánto poner en una moneda para completar lo que falta.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ RESPETA EL REDONDEO QUE ELIGIÓ EL DUEÑO. NO LO PISA.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Hubo un intento de forzar aquí el redondeo hacia arriba, para que el botón
 * «Completar» no dejara nunca el pago corto. El dueño lo rechazó y tenía razón:
 * en Monedas se elige entre «normal», «hacia arriba (favorece al negocio)» y
 * «hacia abajo (favorece al cliente)». Forzar uno de los tres aquí convierte
 * ese ajuste en mentira — quien eligió favorecer al cliente le estaría cobrando
 * de más sin saberlo. Lo dijo así: «solo tienen que ser precisas».
 *
 * Y precisas ya lo son: la diferencia que deja este redondeo SIEMPRE cabe
 * dentro de `toleranciaPorRedondeo`, que es justo un paso de redondeo por
 * moneda. Comprobado barriendo tasas, incrementos, modos y totales.
 */
export function montoParaCompletar(
  faltaEnPrincipal: number,
  moneda: TenantCurrency,
): number {
  const tasa = toNumber(moneda.current_rate);
  if (!Number.isFinite(faltaEnPrincipal) || faltaEnPrincipal <= 0) return 0;
  if (!Number.isFinite(tasa) || tasa <= 0) return 0;

  return redondearMoneda(faltaEnPrincipal * tasa, moneda);
}

/**
 * Redondea un monto según cómo se cobra realmente en esa moneda.
 *
 * Replica `round_for_display()` de `app/core/money.py`. Se calcula aquí y no en
 * el servidor porque el cajero necesita ver el vuelto cambiar mientras escribe,
 * sin esperar una petición por cada tecla.
 *
 * El backend valida el resultado con su propia aritmética exacta, así que un
 * desajuste de milésimas no puede colarse: se rechaza o se registra como ajuste
 * por redondeo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ SE LE QUITA EL RUIDO DE LA COMA FLOTANTE ANTES DE REDONDEAR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * En la computadora 1,10 × 100 no da 110: da 110,00000000000001. Redondeando
 * «hacia arriba», eso son 111 Bs — un bolívar entero de más por una cola que no
 * existe. Hacia abajo pasaba al revés. El servidor calcula con decimales
 * exactos, así que la pantalla pedía un monto y el recibo decía otro. Lo
 * encontró la auditoría.
 *
 * Una millonésima de escalón no es dinero de nadie: es ruido. Se borra antes de
 * decidir hacia dónde se redondea, y otra vez al final, para que un paso de
 * 0,10 no deje 0,30000000000000004. Ver `redondearMoneda.test.ts`, que compara
 * miles de casos con la cuenta exacta.
 */
export function redondearMoneda(
  valor: number,
  // Solo lo que usa: así la página pública del catálogo web redondea con esta
  // MISMA función sin cargar la moneda completa del negocio.
  moneda: Pick<TenantCurrency, "rounding_mode" | "rounding_increment" | "decimals">,
): number {
  const modo = moneda.rounding_mode;
  const incremento = moneda.rounding_increment ? toNumber(moneda.rounding_increment) : 0;

  const aplicar = (n: number): number => {
    const limpio = sinRuido(n);
    if (modo === "ceil") return Math.ceil(limpio);
    if (modo === "floor") return Math.floor(limpio);
    return Math.round(limpio);
  };

  if (incremento > 0) {
    return sinRuido(aplicar(valor / incremento) * incremento);
  }

  const factor = 10 ** moneda.decimals;
  return aplicar(valor * factor) / factor;
}

/** Deja el número en seis decimales: lo que queda más allá es error de cálculo. */
function sinRuido(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

/**
 * Cuánta diferencia hay que tolerar por el redondeo de cada moneda.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ NO SIRVE UN NÚMERO FIJO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La pantalla de cobrar usaba `0,005` —medio céntimo— para decidir si una
 * cuenta cuadra. Ese número no significa nada: la diferencia que puede meter un
 * cobro depende de **cómo se redondea la moneda con la que se paga**.
 *
 * Si los bolívares se entregan de uno en uno y el dólar está a 56,80, cobrar en
 * bolívares puede desviar hasta 1/56,80 = 0,0176 — más del triple del margen
 * que la pantalla toleraba. Y si se redondean de 5 en 5, hasta 0,088.
 *
 * El resultado era una venta bloqueada sin explicación: total $13,53, el cliente
 * paga en bolívares, Softfi le cobra 769 Bs porque 768,5 no se puede entregar, y
 * sobran ocho milésimas de dólar. La pantalla pedía elegir en qué moneda dar un
 * vuelto de $0,00 y el botón de confirmar se quedaba muerto, con el cliente
 * delante y sin un solo aviso.
 *
 * Esta es **la misma regla que aplica el servidor** (`_tolerancia_por_redondeo`
 * en `pos/service.py`): un paso de redondeo de cada moneda usada, convertido a
 * la moneda de referencia. Ni más —se colarían errores de verdad— ni menos —
 * sería imposible cobrar en bolívares.
 *
 * ⚠️ Tienen que seguir diciendo lo mismo. Si un día el servidor cambia su
 * tolerancia y esto no, la pantalla dejará pasar ventas que el servidor
 * rechaza, o al revés: bloqueará ventas que el servidor aceptaría.
 */
export function toleranciaPorRedondeo(monedas: TenantCurrency[]): number {
  // Margen mínimo por la aritmética de coma flotante, igual que el servidor.
  let tolerancia = 0.0001;

  for (const m of monedas) {
    const tasa = toNumber(m.current_rate);
    if (tasa <= 0) continue;

    const incremento = m.rounding_increment ? toNumber(m.rounding_increment) : 0;
    const paso = incremento > 0 ? incremento : 10 ** -m.decimals;

    /*
      ⚠️ LA MONEDA PRINCIPAL APORTA MEDIO PASO, NO UNO (2026-09-25).

      Pagar en la moneda de los precios no convierte nada, así que no redondea
      nada: con un paso entero, $9,59 por una venta de $9,60 salía «Pago
      exacto» —un centavo regalado en cada venta—, y el dueño no quiere
      problemas con clientes. Medio paso deja pasar solo lo que no se puede
      cobrar: los totales con fracción de centavo que dejan los impuestos.
      Las demás monedas siguen con su paso entero: convertir a bolívares sí
      redondea.

      ⚠️ La misma regla está en el servidor (`_tolerancia_por_redondeo`).
    */
    tolerancia += (m.is_primary ? paso / 2 : paso) / tasa;
  }

  return tolerancia;
}

/** Convierte un monto de una moneda a la de referencia. */
export function aPrincipal(monto: number, tasa: string | null): number {
  const t = toNumber(tasa);
  if (t <= 0) return 0;
  return monto / t;
}

/** Da formato a un porcentaje: "40" -> "40%" */
export function formatPercent(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return "—";
  const n = toNumber(valor);
  return `${new Intl.NumberFormat("es-VE", { maximumFractionDigits: 2 }).format(n)}%`;
}

/** Da formato a una cantidad de inventario, quitando los ceros sobrantes. */
export function formatQty(valor: string | number | null | undefined): string {
  const n = toNumber(valor);
  return new Intl.NumberFormat("es-VE", { maximumFractionDigits: 3 }).format(n);
}


/**
 * Muestra una fecha SIN hora ("2026-08-06") en el calendario local.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ NO VALE `new Date(iso).toLocaleDateString()`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque JavaScript interpreta "2026-08-06" como **medianoche en UTC**. Al
 * mostrarla en Caracas (UTC−4) se convierte en las 8 de la noche del día 5, y
 * un cobro registrado hoy aparece con la fecha de ayer.
 *
 * En un libro de cobros eso no es un detalle: es un número que no cuadra con el
 * banco, y a partir de ahí ya no se cree ninguna fecha de la pantalla.
 *
 * La solución es construir la fecha con los tres números por separado, que sí
 * crea medianoche LOCAL.
 */
export function formatFecha(
  iso: string | null | undefined,
  opciones: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short" },
): string {
  if (!iso) return "";

  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const fecha = partes
    ? new Date(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]))
    : new Date(iso);

  return fecha.toLocaleDateString("es-VE", opciones);
}


/**
 * La hora en formato de 12 horas, como se dice en voz alta.
 *
 * "14h" es notación de horario de trenes. Un dueño de bodega piensa y habla en
 * "2 pm", y un reporte que le obliga a traducir mentalmente cada barra es un
 * reporte que se mira menos.
 *
 * Se abrevia sin espacio ni puntos —"2pm", no "2:00 p. m."— porque va debajo de
 * una barra de veinte píxeles de ancho y ahí no cabe nada más largo.
 */
export function formatHora12(hora: number | string): string {
  const h = typeof hora === "number" ? hora : parseInt(hora, 10);
  if (Number.isNaN(h)) return String(hora);

  const sufijo = h < 12 ? "am" : "pm";
  const doce = h % 12 === 0 ? 12 : h % 12;
  return `${doce}${sufijo}`;
}
